import React from 'react';
import { useParams } from 'react-router';
import { usePrefixedNavigate as useNavigate } from '../../../src/hooks/usePrefixedNavigate';
import { useAuth } from '@edusense/shared-auth-core';
import { LoadingSpinner, ErrorDisplay } from "@edusense/shared-ui-kit";
import { PageHeader, SocialHeader } from '@edusense/shared-ui-layout';
import { CourseCard } from '@edusense/features-course';
import { Button } from '@edusense/shared-ui-kit';
import { Button as HeroButton } from '@heroui/react';
import Users from 'lucide-react/dist/esm/icons/users';
import BookmarkPlus from 'lucide-react/dist/esm/icons/bookmark-plus';
import BookmarkMinus from 'lucide-react/dist/esm/icons/bookmark-minus';
import ReactMarkdown from 'react-markdown';
import { transformUserSummaryToAuthorInfo } from '@edusense/entities-user';
import { useRoadmapDetail } from './useRoadmapDetail';
import {
  RightHeaderContent,
  SubscriptionCount,
  ContentWrapper,
  Description,
  ContentSection,
  OrderOverlay,
  CourseCardWrapperWithOverlay,
  RoadmapInfoCard,
  RoadmapCardTitle,
} from './styles';

/**
 * 로드맵 상세 페이지 메인 컴포넌트
 * React Query와 현대적인 패턴을 사용한 리팩토링 버전
 */
const RoadmapDetailPage: React.FC = () => {
  const { roadmapId } = useParams<{ roadmapId: string }>();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [isHovered, setIsHovered] = React.useState(false);

  // 새로운 React Query 기반 커스텀 훅 사용
  const {
    roadmap,
    isLoading,
    error,
    isSubscribing,
    isDeleting,
    loadRoadmapDetail,
    handleSubscribeToggle,
    handleDeleteRoadmap,
  } = useRoadmapDetail(roadmapId);

  // 내비게이션 핸들러들
  const handleGoBack = () => navigate(-1);
  
  const handleDelete = async () => {
    const success = await handleDeleteRoadmap();
    if (success) navigate(-1);
  };

  // 로드맵 소유자 확인
  const isMyRoadmap = currentUser && roadmap?.summary?.creator?.basicInfo?.id === currentUser.id;

  /**
   * 메인 콘텐츠 렌더링
   */
  const renderContent = () => {
    if (isLoading) {
      return <div style={{ padding: '2rem', textAlign: 'center' }}><LoadingSpinner /></div>;
    }

    if (error) {
      return (
        <ContentWrapper>
          <ErrorDisplay message={error} onRetry={loadRoadmapDetail} />
        </ContentWrapper>
      );
    }

    if (!roadmap?.summary) {
      return (
        <ContentWrapper>
          <ErrorDisplay message="로드맵 정보를 찾을 수 없습니다." />
        </ContentWrapper>
      );
    }

    const { summary, content, courses } = roadmap;

    // UserSummary를 SocialHeader용 타입으로 변환
    const creatorInfoForHeader = summary.creator 
      ? transformUserSummaryToAuthorInfo(summary.creator)
      : undefined;

    return (
      <ContentWrapper>
        {/* 로드맵 기본 정보 카드 */}
        <RoadmapInfoCard>
          {creatorInfoForHeader && summary.createdAt && (
            <div style={{ marginBottom: '1rem' }}>
              <SocialHeader
                author={creatorInfoForHeader}
                createdAt={new Date(summary.createdAt)}
                size="md"
                showTimestamp={true}
                showActions={false}
                variant="user"
                onAuthorClick={(author) => navigate(`/profile/${author.username}`)}
              />
            </div>
          )}
          <RoadmapCardTitle>{summary.title}</RoadmapCardTitle>
          {summary.description && (
            <Description>{summary.description}</Description>
          )}
        </RoadmapInfoCard>

        {/* 로드맵 상세 콘텐츠 (마크다운) - description이 있으면 숨김 */}
        {content && !summary.description && (
          <ContentSection>
            <ReactMarkdown>{content}</ReactMarkdown>
          </ContentSection>
        )}

        {/* 포함된 강의 목록 */}
        {courses && courses.length > 0 ? (
          courses.map((course) => (
            <CourseCardWrapperWithOverlay key={course.id}>
              <CourseCard course={course} />
              {course.order !== undefined && (
                <OrderOverlay>{course.order}</OrderOverlay>
              )}
            </CourseCardWrapperWithOverlay>
          ))
        ) : (
          <p>포함된 강의가 없습니다.</p>
        )}
      </ContentWrapper>
    );
  };

  /**
   * 헤더 우측 액션 버튼들 렌더링
   */
  const renderHeaderActions = () => {
    if (!roadmap?.summary) return null;

    return (
      <RightHeaderContent>
        {/* 구독자 수 표시 */}
        {(roadmap.summary.subscriptionCount !== undefined && roadmap.summary.subscriptionCount >= 0) && (
          <SubscriptionCount title={`구독자 ${roadmap.summary.subscriptionCount}명`}>
            <Users />
            {roadmap.summary.subscriptionCount}
          </SubscriptionCount>
        )}
        
        {/* 인증된 사용자용 액션 버튼들 */}
        {isAuthenticated && (
          isMyRoadmap ? (
            // 내 로드맵인 경우: 수정, 삭제 버튼
            <>
              <Button 
                onClick={() => navigate(`/learn/roadmap/${roadmapId}/edit`)} 
                size="sm" 
                variant="outline"
                title="로드맵 수정하기"
              >
                수정하기
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isDeleting}
                size="sm"
                variant="danger"
                title="로드맵 삭제하기"
                style={{ marginLeft: '0.5rem' }}
              >
                {isDeleting ? <LoadingSpinner /> : '삭제하기'}
              </Button>
            </>
          ) : (
            // 다른 사람 로드맵인 경우: 구독/구독취소 버튼
            <HeroButton
              onClick={handleSubscribeToggle}
              disabled={isSubscribing}
              variant={roadmap.summary.isSubscribed ? 'bordered' : 'solid'}
              color={roadmap.summary.isSubscribed ? (isHovered ? 'danger' : 'default') : 'primary'}
              radius="full"
              size="sm"
              aria-label={roadmap.summary.isSubscribed ? '구독 취소' : '구독하기'}
              data-testid="roadmap-subscribe-button"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              startContent={
                isSubscribing ? (
                  <LoadingSpinner />
                ) : roadmap.summary.isSubscribed ? (
                  <BookmarkMinus size={16} />
                ) : (
                  <BookmarkPlus size={16} />
                )
              }
            >
              {(() => {
                const isSubscribed = Boolean(roadmap.summary.isSubscribed);
                return isSubscribed ? (isHovered ? '구독 취소' : '구독 중') : '구독하기';
              })()}
            </HeroButton>
          )
        )}
      </RightHeaderContent>
    );
  };

  return (
    <div className="roadmap-detail-page">
      {/* 페이지 헤더 */}
      <PageHeader
        title="로드맵"
        showBackButton={true}
        onBackClick={handleGoBack}  
        rightContent={renderHeaderActions()}
      />
      
      {/* 메인 콘텐츠 */}
      {renderContent()}
    </div>
  );
};

export default RoadmapDetailPage;